import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { GoogleOAuthProvider } from '@react-oauth/google';

const isJsonRpcInternalError = (reason) => {
  if (!reason || typeof reason !== 'object') return false;
  const code = reason.code;
  const message = String(reason.message || '');
  return code === -32603 && message.includes('Internal JSON-RPC error');
};

// Override console.error to prevent React Error Overlay from catching [object Object]
const originalConsoleError = console.error;
console.error = (...args) => {
  try {
    const firstArg = args[0];
    const isObjectError = firstArg && Object.prototype.toString.call(firstArg) === '[object Object]';
    const isObjectMessage = typeof firstArg === 'string' && (firstArg === '[object Object]' || firstArg.includes('[object Object]'));
    
    if (isObjectError || isObjectMessage) {
      // Check if it's the specific JSON-RPC error we want to ignore silently
      if (isObjectError && isJsonRpcInternalError(firstArg)) return;
      
      // Log as warning instead of error to bypass overlay
      console.warn('[Suppressed Error Overlay]', ...args);
      return;
    }
  } catch (e) {
    // ignore
  }
  originalConsoleError.apply(console, args);
};

if (!window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__) {
  window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ = {};
}
if (window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__) {
  window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.onError = () => {};
  window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.onUnhandledRejection = () => {};
}

window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && Object.prototype.toString.call(event.reason) === '[object Object]') {
    event.preventDefault();
    event.stopImmediatePropagation(); // Prevent other listeners
    if (isJsonRpcInternalError(event.reason)) return;
    
    let content = '';
    try {
      content = JSON.stringify(event.reason, null, 2);
    } catch (e) {
      content = String(event.reason);
    }
    console.warn('Unhandled Rejection with [object Object]. Content:', content);
  }
});

window.addEventListener('error', (event) => {
  const isObjectError = event.error && Object.prototype.toString.call(event.error) === '[object Object]';
  const isObjectMessage = String(event.message || '') === '[object Object]';
  if (isObjectError || isObjectMessage) {
    if (isObjectError && isJsonRpcInternalError(event.error)) return;
    event.preventDefault();
    event.stopImmediatePropagation(); // Prevent other listeners
    
    let content = '';
    try {
      content = isObjectError ? JSON.stringify(event.error, null, 2) : String(event.message || '');
    } catch (e) {
      content = isObjectError ? String(event.error) : String(event.message || '');
    }
    const meta = {
      source: event.filename || '',
      line: event.lineno || 0,
      col: event.colno || 0
    };
    console.warn('Uncaught Error with [object Object]. Content:', content, meta);
  }
});

window.onerror = (message, source, lineno, colno, error) => {
  const isObjectError = error && Object.prototype.toString.call(error) === '[object Object]';
  const isObjectMessage = String(message || '') === '[object Object]';
  if (isObjectError || isObjectMessage) {
    if (isObjectError && isJsonRpcInternalError(error)) return true;
    // Log details as warn
    let content = '';
    try {
      content = isObjectError ? JSON.stringify(error, null, 2) : String(message || '');
    } catch (e) {
      content = isObjectError ? String(error) : String(message || '');
    }
    console.warn('Uncaught Error (window.onerror) with [object Object].', content, { source, lineno, colno });
    return true; // Suppress default handling
  }
  return false;
};

window.onunhandledrejection = (event) => {
  if (event && event.reason && Object.prototype.toString.call(event.reason) === '[object Object]') {
    if (typeof event.preventDefault === 'function') event.preventDefault();
    if (isJsonRpcInternalError(event.reason)) return true;
    // Log details as warn
    let content = '';
    try {
      content = JSON.stringify(event.reason, null, 2);
    } catch (e) {
      content = String(event.reason);
    }
    console.warn('Unhandled Rejection (window.onunhandledrejection) with [object Object].', content);
    return true;
  }
  return false;
};

const Root = () => {
  const [clientId, setClientId] = useState(null);
  
  useEffect(() => {
    // Determine API_BASE similar to App.js logic, but we need it before App renders
    // We assume the server is reachable at /api/auth/config relative to current origin
    // or fallback to localhost:4000 logic if on dev
    const getServerUrl = () => {
       // If env var is set (dev build), use it
       if (process.env.REACT_APP_SERVER_URL) return process.env.REACT_APP_SERVER_URL;
       // If running on localhost (dev), assume port 4000
       if (window.location.hostname === 'localhost') return 'http://localhost:4000';
       // Default to current protocol/hostname with port 4000
      return `${window.location.protocol}//${window.location.hostname}:4000`;
    };

    const base = getServerUrl();
    
    fetch(`${base}/api/auth/config`)
      .then(r => r.json())
      .then(data => {
        if (data && typeof data.googleClientId === 'string' && data.googleClientId) {
          setClientId(data.googleClientId);
        } else {
          // Fallback if API fails or returns empty
          console.error('Failed to get Google Client ID from server config or invalid format');
        }
      })
      .catch((err) => {
        console.error('Failed to fetch config from server', err);
      });
  }, []);

  if (clientId === null) return null; // or loading spinner

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <App />
    </GoogleOAuthProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
